import { useCallback, useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, Tooltip } from '@mui/material';

import {
    DataTable,
    ErrorMessage,
    type TableColumn,
    ActivateIcon,
    TrashIcon,
    ResetIcon,
} from '@/src/ui';
import type { StudentExamAttemptsDTO } from '../models/attemptDetailsDTOs';
import { useAuth } from '@/src/app/context/useAuth';
import {
    Constants,
    ReportHeaderStyle,
    ReportCellStyle,
    utcToLocal,
    formatOptions,
} from '@/src/utils';
import { apiRequest } from '@/src/services';

interface AttemptDetailsModalProps {
    open: boolean;
    onClose: (changesMade: boolean) => void;
    student: StudentExamAttemptsDTO;
}

export default function AttemptDetailsModal({ open, onClose, student }: AttemptDetailsModalProps) {
    const { user } = useAuth();
    const [attempts, setAttempts] = useState(student.attempts ?? []);
    const [error, setError] = useState<string>('');

    // Only refresh page on close if something was modified
    const [changesMade, setChangesMade] = useState(false);

    useEffect(() => {
        setAttempts(student.attempts ?? []);
    }, [student, open]);

    const handleDeleteAttempt = async (attemptId: number) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete this attempt: ${attemptId}?`
        );
        if (!confirmed) return;

        try {
            await apiRequest('DELETE', `assessments/studentExamAttempts/${attemptId}`);
            setAttempts((prev) => prev.filter((a) => a.attemptId !== attemptId));
            setError('');
            setChangesMade(true);
        } catch {
            setError('Failed to delete attempt');
        }
    };

    const handleSubmitAttempt = async (attemptId: number, mode: 'submit' | 'reopen') => {
        const confirmed = window.confirm(
            `Are you sure you want to ${mode} this attempt: ${attemptId}?`
        );
        if (!confirmed) return;

        try {
            await apiRequest(
                'POST',
                `assessments/studentExamAttempts/${attemptId}/${mode === 'submit' ? 'submit' : 'unsubmit'}`
            );
            const nowUtc = new Date().toISOString();
            setAttempts((prev) =>
                prev.map((a) =>
                    a.attemptId === attemptId
                        ? { ...a, submittedStatus: mode === 'submit' ? 3 : 2, endDTUTC: nowUtc }
                        : a
                )
            );
            setError('');
            setChangesMade(true);
        } catch {
            setError(`Failed to ${mode} attempt`);
        }
    };

    const formatActionsRow = useCallback(
        (row: any) => {
            const submitButton =
                row.submittedStatus !== 3 ? (
                    <Tooltip title="Submit Attempt" arrow>
                        <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            className="icon-button"
                            onClick={() => handleSubmitAttempt(row.attemptId, 'submit')}
                        >
                            <span role="img" aria-label="submit">
                                <ActivateIcon style={{ width: '28px', height: '28px' }} />
                            </span>
                        </button>
                    </Tooltip>
                ) : null;

            const unsubmitButton =
                row.submittedStatus === 3 ? (
                    <Tooltip title="Reopen Attempt" arrow>
                        <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            className="icon-button"
                            onClick={() => handleSubmitAttempt(row.attemptId, 'reopen')}
                        >
                            <span role="img" aria-label="reopen">
                                <ResetIcon style={{ width: '28px', height: '28px' }} />
                            </span>
                        </button>
                    </Tooltip>
                ) : null;

            const deleteButton =
                user?.role === Constants.ROLES.SUPER_ADMIN ? (
                    <Tooltip title="Delete Attempt" arrow>
                        <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            className="icon-button"
                            onClick={() => handleDeleteAttempt(row.attemptId)}
                        >
                            <span role="img" aria-label="delete">
                                <TrashIcon style={{ width: '28px', height: '28px' }} />
                            </span>
                        </button>
                    </Tooltip>
                ) : null;

            const content =
                submitButton || unsubmitButton || deleteButton ? (
                    <>
                        {submitButton}
                        {unsubmitButton}
                        {deleteButton}
                    </>
                ) : (
                    <>–</>
                );

            return (
                <Box display="flex" justifyContent="center" gap={1}>
                    {content}
                </Box>
            );
        },
        [user?.role]
    );

    const columns: TableColumn[] = [
        {
            dataKey: 'completedSummary',
            header: 'Completed',
            align: 'center',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => `${row.completedCount ?? '—'} / ${row.totalQuestions ?? '—'}`,
        },
        {
            dataKey: 'submittedStatus',
            header: 'Status',
            align: 'center',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => (row.submittedStatus === 3 ? 'Submitted' : 'Not Submitted'),
        },
        {
            dataKey: 'startDTUTC',
            header: 'Start Time',
            align: 'left',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => {
                if (!row.startDTUTC) return '-';
                return utcToLocal(row.startDTUTC).toLocaleString(undefined, formatOptions);
            },
        },
        {
            dataKey: 'endDTUTC',
            header: 'End Time',
            align: 'left',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => {
                if (!row.endDTUTC) return '-';
                return utcToLocal(row.endDTUTC).toLocaleString(undefined, formatOptions);
            },
        },
        {
            dataKey: 'actions',
            header: 'Actions',
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => formatActionsRow(row),
        },
    ];

    return (
        <Modal open={open} onClose={() => onClose(changesMade)}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    width: '80%',
                    height: '80%',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <Box mb={3}>
                    <Typography variant="h6" fontWeight="bold">
                        Student: {student.studentName} ({student.studentId})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Assessment: <strong>{student.examName}</strong> | Total Attempts:{' '}
                        <strong>{attempts.length}</strong>
                    </Typography>
                </Box>

                {/* Error Alert */}
                <ErrorMessage error={error} />

                {/* Table */}
                {attempts.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={attempts}
                        tableName={`Attempts for ${student.studentName}`}
                        alternatingRowColor
                        defaultSortColumn="endDTUTC"
                        defaultSortDirection="desc"
                    />
                ) : (
                    <Typography color="text.secondary" align="center" mt={4}>
                        No attempts found for this student.
                    </Typography>
                )}

                {/* Footer */}
                <Box textAlign="right" mt={3}>
                    <Button variant="outlined" onClick={() => onClose(changesMade)}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
